import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL, getAuthHeaders, formatDateTime } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { useApiMessages } from "../hooks/useApiMessages";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { UserCheck, MapPin, Clock, User, Eye, Image, Trash2, Search, Calendar, Grid3x3, List } from "lucide-react";
import { toast } from "sonner";

export const GuardLoginRecords = () => {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const { can, canManageCompanies } = useAuth();
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [selectedUser, setSelectedUser] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isPhotoOpen, setIsPhotoOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "table"
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (selectedCompany && selectedCompany !== "all") {
        params.append("company_id", selectedCompany);
      }
      if (selectedUser) {
        params.append("user_id", selectedUser);
      }
      if (startDate) {
        params.append("start_date", startDate);
      }
      if (endDate) {
        params.append("end_date", endDate);
      }
      
      const url = params.toString() 
        ? `${API_URL}/guard/login-records?${params.toString()}`
        : `${API_URL}/guard/login-records`;
      
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setRecords(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching records:", error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`, { headers: getAuthHeaders() });
      const usersData = Array.isArray(response.data) ? response.data : [];
      setUsers(usersData.filter((u) => u.role === "vigilante"));
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/companies`, { headers: getAuthHeaders() });
      setCompanies(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanies([]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchUsers(), fetchCompanies()]);
    };
    loadData();
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [selectedCompany, selectedUser, startDate, endDate]);

  const getUserName = (userId) => {
    if (!Array.isArray(users)) return "Desconocido";
    const user = users.find((u) => u.id === userId);
    return user?.name || "Desconocido";
  };

  const filteredRecords = Array.isArray(records) 
    ? (selectedUser
        ? records.filter((r) => r.user_id === selectedUser)
        : records)
    : [];

  // Pagination logic
  const totalPages = Math.ceil((Array.isArray(filteredRecords) ? filteredRecords.length : 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRecords = Array.isArray(filteredRecords)
    ? (viewMode === "grid" 
        ? filteredRecords.slice(startIndex, endIndex)
        : filteredRecords)
    : [];

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCompany, selectedUser, startDate, endDate, viewMode]);

  const openPhoto = (record) => {
    setSelectedRecord(record);
    setIsPhotoOpen(true);
  };

  const handleDelete = async () => {
    if (!recordToDelete) return;
    try {
      await axios.delete(`${API_URL}/guard/login-records/${recordToDelete.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success(t("pages.guardLoginRecords.recordDeleted"));
      setIsDeleteOpen(false);
      setRecordToDelete(null);
      fetchRecords();
    } catch (error) {
      toast.error(getErrorMessage(error, "api.errors.deleteError"));
    }
  };

  return (
    <div className="space-y-6" data-testid="guard-login-records-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            {t("pages.guardLoginRecords.title")}
          </h1>
          <p className="text-slate-600 mt-1">
            {t("pages.guardLoginRecords.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Label htmlFor="view-mode" className="text-sm text-slate-600 flex items-center gap-2 cursor-pointer">
            <List className="h-4 w-4" />
            {t("pages.guardLoginRecords.table")}
          </Label>
          <Switch
            id="view-mode"
            checked={viewMode === "grid"}
            onCheckedChange={(checked) => setViewMode(checked ? "grid" : "table")}
          />
          <Label htmlFor="view-mode" className="text-sm text-slate-600 flex items-center gap-2 cursor-pointer">
            <Grid3x3 className="h-4 w-4" />
            {t("pages.guardLoginRecords.grid")}
          </Label>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Filtro por compañía - Solo para admin y revendedor */}
            {canManageCompanies() && (
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">{t("pages.companies.name")}</Label>
                <Select
                  value={selectedCompany}
                  onValueChange={(value) => {
                    setSelectedCompany(value);
                    setLoading(true);
                  }}
                >
                  <SelectTrigger data-testid="filter-company">
                    <SelectValue placeholder={t("pages.guardLoginRecords.all")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("pages.routes.allCompanies")}</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label className="text-sm text-slate-600 mb-1 block">{t("pages.guardLoginRecords.guard")}</Label>
              <Select
                value={selectedUser}
                onValueChange={(value) => setSelectedUser(value === "all" ? "" : value)}
              >
                <SelectTrigger data-testid="filter-guard">
                  <SelectValue placeholder={t("pages.guardLoginRecords.all")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.guardLoginRecords.allGuards")}</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-sm text-slate-600 mb-1 block">{t("pages.guardLoginRecords.from")}</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            
            <div>
              <Label className="text-sm text-slate-600 mb-1 block">{t("pages.guardLoginRecords.to")}</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
            
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedCompany("all");
                  setSelectedUser("");
                  setStartDate("");
                  setEndDate("");
                }}
              >
                {t("pages.guardLoginRecords.clearFilters")}
              </Button>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-3">
            {t("pages.guardLoginRecords.recordsFound", { count: filteredRecords.length })}
          </p>
        </CardContent>
      </Card>

      {/* Records View */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : records.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <UserCheck className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">{t("pages.guardLoginRecords.noRecords")}</p>
          </CardContent>
        </Card>
      ) : viewMode === "grid" ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedRecords.map((record) => (
              <Card key={record.id} className="overflow-hidden card-hover">
                <CardContent className="p-0">
                  {/* Photo thumbnail */}
                  <div
                    className="relative h-48 bg-slate-100 cursor-pointer group"
                    onClick={() => openPhoto(record)}
                  >
                    {record.photo_base64 ? (
                      <img
                        src={record.photo_base64}
                        alt={t("pages.guardLoginRecords.entryPhoto")}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="h-12 w-12 text-slate-300" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Eye className="h-8 w-8 text-white" />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {getUserName(record.user_id)}
                        </p>
                        <p className="text-xs text-slate-500">{t("pages.guardLoginRecords.guard")}</p>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock className="h-4 w-4 text-slate-400" />
                        {formatDateTime(record.timestamp)}
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        <span className="font-mono text-xs">
                          {record.latitude?.toFixed(6)}, {record.longitude?.toFixed(6)}
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => openPhoto(record)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Ver Foto
                      </Button>
                      {can("guard_records.delete") && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => {
                            setRecordToDelete(record);
                            setIsDeleteOpen(true);
                          }}
                          data-testid={`delete-record-${record.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination for Grid View */}
          {filteredRecords.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <p className="text-sm text-slate-600">
                    Mostrando {startIndex + 1} - {Math.min(endIndex, filteredRecords.length)} de {filteredRecords.length} registros
                  </p>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Anterior
                      </Button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter((page) => {
                            // Show first page, last page, current page, and pages around current
                            return (
                              page === 1 ||
                              page === totalPages ||
                              (page >= currentPage - 1 && page <= currentPage + 1)
                            );
                          })
                          .map((page, index, array) => {
                            // Add ellipsis if there's a gap
                            const prevPage = array[index - 1];
                            const showEllipsis = prevPage && page - prevPage > 1;
                            return (
                              <div key={page} className="flex items-center gap-1">
                                {showEllipsis && <span className="text-slate-400">...</span>}
                                <Button
                                  variant={currentPage === page ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setCurrentPage(page)}
                                  className="min-w-[40px]"
                                >
                                  {page}
                                </Button>
                              </div>
                            );
                          })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Siguiente
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pages.guardLoginRecords.photo")}</TableHead>
                  <TableHead>{t("pages.guardLoginRecords.guard")}</TableHead>
                  <TableHead>{t("pages.guardLoginRecords.dateTime")}</TableHead>
                  <TableHead>{t("pages.guardLoginRecords.location")}</TableHead>
                  <TableHead className="text-center">{t("pages.guardLoginRecords.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecords.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>
                      <div
                        className="relative w-16 h-16 bg-slate-100 rounded cursor-pointer group"
                        onClick={() => openPhoto(record)}
                      >
                        {record.photo_base64 ? (
                          <img
                            src={record.photo_base64}
                            alt={t("pages.guardLoginRecords.entryPhoto")}
                            className="w-full h-full object-cover rounded"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="h-6 w-6 text-slate-300" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded">
                          <Eye className="h-4 w-4 text-white" />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {getUserName(record.user_id)}
                          </p>
                          <p className="text-xs text-slate-500">{t("pages.guardLoginRecords.guard")}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {formatDateTime(record.timestamp)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        <span className="font-mono text-xs">
                          {record.latitude?.toFixed(6)}, {record.longitude?.toFixed(6)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openPhoto(record)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          {t("pages.guardLoginRecords.view")}
                        </Button>
                        {can("guard_records.delete") && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setRecordToDelete(record);
                              setIsDeleteOpen(true);
                            }}
                            data-testid={`delete-record-${record.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Photo Dialog */}
      <Dialog open={isPhotoOpen} onOpenChange={setIsPhotoOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-orange-500" />
              {t("pages.guardLoginRecords.title")} - {selectedRecord && getUserName(selectedRecord.user_id)}
            </DialogTitle>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4">
              <div className="rounded-lg overflow-hidden bg-slate-100">
                {selectedRecord.photo_base64 ? (
                  <img
                    src={selectedRecord.photo_base64}
                    alt="Foto de entrada"
                    className="w-full"
                  />
                ) : (
                  <div className="h-64 flex items-center justify-center">
                    <Image className="h-16 w-16 text-slate-300" />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">{t("pages.guardLoginRecords.dateTime")}</p>
                  <p className="font-medium">{formatDateTime(selectedRecord.timestamp)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">{t("pages.guardLoginRecords.gpsLocation")}</p>
                  <p className="font-mono text-sm">
                    {selectedRecord.latitude?.toFixed(6)}, {selectedRecord.longitude?.toFixed(6)}
                  </p>
                </div>
              </div>
              <a
                href={`https://www.google.com/maps?q=${selectedRecord.latitude},${selectedRecord.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button variant="outline" className="w-full">
                  <MapPin className="h-4 w-4 mr-2" />
                  {t("pages.guardLoginRecords.viewOnGoogleMaps")}
                </Button>
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.guardLoginRecords.deleteRecord")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("pages.guardLoginRecords.deleteConfirm")}{" "}
              "{recordToDelete && getUserName(recordToDelete.user_id)}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-record"
            >
              {t("pages.guardLoginRecords.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
